import React, { useState } from 'react';
    import { motion } from 'framer-motion';
    import { useAuth } from '../context/AuthContext';
    import SafeIcon from '../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';

    const { FiX, FiMail, FiSend, FiCheck, FiAlertCircle, FiInfo } = FiIcons;

    const ForgotPasswordModal = ({ onClose }) => {
      const [email, setEmail] = useState('');
      const [loading, setLoading] = useState(false);
      const [message, setMessage] = useState(null);
      const { sendPasswordReset } = useAuth();

      const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) return;
        
        setLoading(true);
        setMessage(null);
        
        try {
          const result = await sendPasswordReset(email.trim());
          if (result.success) {
            setMessage({
              type: 'success',
              text: 'Reset instructions have been sent to your email. Please check your Inbox and Spam folder.'
            });
          } else {
            setMessage({
              type: 'error',
              text: result.message
            });
          }
        } catch (error) {
          setMessage({
            type: 'error',
            text: 'An unexpected connection error occurred.'
          });
        } finally {
          setLoading(false);
        }
      };

      return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }} 
            className="bg-white rounded-[2rem] shadow-2xl max-w-md w-full overflow-hidden"
          >
            <div className="flex items-center justify-between p-8 border-b border-gray-100">
              <h2 className="text-2xl font-black text-gray-900 tracking-tight">Security</h2>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl text-gray-400 hover:text-gray-600 transition-all">
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              {message ? (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-5 rounded-2xl border-2 ${
                    message.type === 'success' 
                      ? 'bg-emerald-50 border-emerald-100 text-emerald-800' 
                      : 'bg-rose-50 border-rose-100 text-rose-800'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <SafeIcon icon={message.type === 'success' ? FiCheck : FiAlertCircle} className="text-xl" />
                    <span className="font-black uppercase text-[10px] tracking-widest">
                      {message.type === 'success' ? 'Email Dispatched' : 'Request Failed'}
                    </span>
                  </div>
                  <p className="text-sm font-bold leading-relaxed">{message.text}</p>
                </motion.div>
              ) : (
                <div className="bg-indigo-50 p-5 rounded-2xl flex gap-4">
                  <SafeIcon icon={FiInfo} className="text-indigo-600 text-xl flex-shrink-0 mt-0.5" />
                  <p className="text-xs font-bold text-indigo-800 leading-relaxed">
                    Enter your registered email. We will send a secure Firebase link to reset your password.
                  </p>
                </div>
              )}

              {(!message || message.type !== 'success') && (
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Account Email</label>
                  <div className="relative">
                    <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input 
                      type="email" 
                      value={email} 
                      onChange={(e) => setEmail(e.target.value)} 
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none transition-all font-bold" 
                      placeholder="name@expertguru.com" 
                      required 
                      disabled={loading} 
                    />
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-3 pt-4">
                {(!message || message.type !== 'success') && (
                  <button 
                    type="submit" 
                    disabled={loading || !email} 
                    className="w-full flex items-center justify-center gap-3 py-4 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-indigo-700 shadow-xl shadow-indigo-100 transition-all disabled:opacity-50"
                  >
                    {loading ? (
                      <SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-lg" />
                    ) : (
                      <>
                        <SafeIcon icon={FiSend} className="text-lg" />
                        Send Reset Link
                      </>
                    )}
                  </button>
                )}
                <button 
                  type="button" 
                  onClick={onClose} 
                  className="w-full py-2 text-gray-400 font-bold text-xs uppercase tracking-widest hover:text-gray-600 transition-colors"
                >
                  {message?.type === 'success' ? 'Back to Login' : 'Cancel'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      );
    };

    export default ForgotPasswordModal;